import { useCallback, useRef, useState } from 'react';

interface UseHistoryStateReturn<T> {
  state: T;
  set: (newStateOrUpdater: T | ((prev: T) => T), addToHistory?: boolean) => void;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  setInitialState: (newInitialState: T) => void;
  canUndo: boolean;
  canRedo: boolean;
}

export default function useHistoryState<T>(initialState: T): UseHistoryStateReturn<T> {
  const initialStateRef = useRef<T>(initialState);
  const stateRef = useRef<T>(initialState);
  const historyRef = useRef<T[]>([initialState]);
  const currentIndexRef = useRef<number>(0);

  const [state, setState] = useState<T>(initialState);
  const [history, setHistory] = useState<T[]>([initialState]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const set = useCallback(
    (newStateOrUpdater: T | ((prev: T) => T), addToHistory: boolean = true) => {
      const nextState =
        typeof newStateOrUpdater === 'function'
          ? (newStateOrUpdater as (prev: T) => T)(stateRef.current)
          : newStateOrUpdater;

      if (nextState === stateRef.current) {
        return;
      }

      stateRef.current = nextState;
      setState(nextState);

      if (addToHistory) {
        const nextHistory = [
          ...historyRef.current.slice(0, currentIndexRef.current + 1),
          nextState
        ];
        const nextIndex = nextHistory.length - 1;

        historyRef.current = nextHistory;
        currentIndexRef.current = nextIndex;

        setHistory(nextHistory);
        setCurrentIndex(nextIndex);
      }
    },
    []
  );

  const undo = useCallback(() => {
    if (currentIndexRef.current <= 0) {
      return;
    }

    const nextIndex = currentIndexRef.current - 1;
    const nextState = historyRef.current[nextIndex];

    currentIndexRef.current = nextIndex;
    stateRef.current = nextState;

    setCurrentIndex(nextIndex);
    setState(nextState);
  }, []);

  const redo = useCallback(() => {
    if (currentIndexRef.current >= historyRef.current.length - 1) {
      return;
    }

    const nextIndex = currentIndexRef.current + 1;
    const nextState = historyRef.current[nextIndex];

    currentIndexRef.current = nextIndex;
    stateRef.current = nextState;

    setCurrentIndex(nextIndex);
    setState(nextState);
  }, []);

  const clear = useCallback(() => {
    const resetState = initialStateRef.current;

    stateRef.current = resetState;
    historyRef.current = [resetState];
    currentIndexRef.current = 0;

    setState(resetState);
    setHistory([resetState]);
    setCurrentIndex(0);
  }, []);

  const setInitialState = useCallback((newInitialState: T) => {
    initialStateRef.current = newInitialState;

    stateRef.current = newInitialState;
    historyRef.current = [newInitialState];
    currentIndexRef.current = 0;

    setState(newInitialState);
    setHistory([newInitialState]);
    setCurrentIndex(0);
  }, []);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    state,
    set,
    undo,
    redo,
    clear,
    setInitialState,
    canUndo,
    canRedo
  };
}
